import React from 'react';
import styled, { keyframes } from 'styled-components';
import { ProductsData } from '../types/siteDataTypes';
import { Link } from 'react-router-dom';
import { Section, Container, SectionHeading } from '../styles/layout';

interface ProductsProps {
  data?: ProductsData;
}

/* ─── Layout ─────────────────────────────────────────────────────────────────── */

const SectionTitle = SectionHeading;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  align-items: start;
`;

/* ─── Cards ──────────────────────────────────────────────────────────────────── */

const FeaturedCard = styled.div`
  background: linear-gradient(145deg, #0a1420, #1c3650);
  border: 1px solid rgba(129, 127, 253, 0.3);
  border-radius: 18px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 24px 48px rgba(10, 20, 32, 0.3);
`;

const Card = styled.div`
  background: linear-gradient(145deg, rgba(239, 244, 249, 0.6), rgba(223, 231, 240, 0.6));
  border: 1px solid #cfd9e4;
  border-radius: 18px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 20px 38px rgba(34, 50, 70, 0.12);
`;

/* ─── Live badge ─────────────────────────────────────────────────────────────── */

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
`;

const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #34d399;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(52, 211, 153, 0.1);
  border: 1px solid rgba(52, 211, 153, 0.25);
  width: fit-content;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #34d399;
    display: inline-block;
    animation: ${pulse} 2s infinite;
  }
`;

/* ─── Text ───────────────────────────────────────────────────────────────────── */

const ProductName = styled.h3<{ $featured?: boolean }>`
  margin: 0;
  color: ${({ $featured }) => ($featured ? '#ffffff' : '#1f2b3a')};
`;

const Tagline = styled.p<{ $featured?: boolean }>`
  margin: 0;
  color: ${({ $featured }) => ($featured ? 'rgba(255,255,255,0.62)' : '#4a5a6b')};
  line-height: 1.55;
  font-size: 15px;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BulletItem = styled.li<{ $featured?: boolean }>`
  color: ${({ $featured }) => ($featured ? 'rgba(255,255,255,0.58)' : '#4a5a6b')};
  line-height: 1.55;
  margin-bottom: 8px;
  padding-left: 16px;
  position: relative;
  font-size: 14px;

  &::before {
    content: '\\2022';
    position: absolute;
    left: 0;
    color: ${({ $featured }) => ($featured ? '#817ffd' : '#5ca9fb')};
  }
`;

/* ─── Actions ────────────────────────────────────────────────────────────────── */

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: auto;
`;

const PrimaryButton = styled.a<{ $featured?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 500;
  font-size: 16px;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  background: ${({ $featured }) => ($featured ? '#817ffd' : '#0f1c2d')};
  color: #ffffff;
  box-shadow: ${({ $featured }) =>
    $featured
      ? '0 8px 24px rgba(129, 127, 253, 0.4)'
      : '0 8px 20px rgba(15, 28, 45, 0.2)'};

  &:hover {
    color: #ffffff;
    transform: translateY(-2px);
    background: ${({ $featured }) => ($featured ? '#9b99ff' : '#1c3650')};
    box-shadow: ${({ $featured }) =>
      $featured
        ? '0 14px 32px rgba(129, 127, 253, 0.55)'
        : '0 14px 28px rgba(15, 28, 45, 0.28)'};
  }
`;

const Status = styled.span`
  display: inline-block;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #c7d1dc;
  color: #4a5a6b;
  font-size: 13px;
`;

/* ─── Component ──────────────────────────────────────────────────────────────── */

const Products: React.FC<ProductsProps> = ({ data }) => {
  return (
    <Section id="products">
      <Container>
        <SectionTitle>
          <h2>{data?.heading || 'Our Products'}</h2>
          {data?.subheading && <p>{data.subheading}</p>}
        </SectionTitle>
        <Grid>
          {data?.items.map((product) => {
            const isFeatured = !product.status; // live products have no status field
            const CardWrapper = isFeatured ? FeaturedCard : Card;

            return (
              <CardWrapper key={product.name}>
                {isFeatured && <LiveBadge>Available now</LiveBadge>}
                <ProductName $featured={isFeatured}>{product.name}</ProductName>
                <Tagline $featured={isFeatured}>{product.tagline}</Tagline>
                <BulletList>
                  {product.bullets.map((bullet, idx) => (
                    <BulletItem key={`${product.name}-${idx}`} $featured={isFeatured}>
                      {bullet}
                    </BulletItem>
                  ))}
                </BulletList>
                {product.status && <Status>{product.status}</Status>}
                <Actions>
                  <PrimaryButton as={Link} to={product.ctaHref} $featured={isFeatured}>
                    {product.ctaLabel}
                  </PrimaryButton>
                </Actions>
              </CardWrapper>
            );
          })}
        </Grid>
      </Container>
    </Section>
  );
};

export default Products;
